import type { ReactNode } from "react";
import { Bg, Cloud, Coin, CoinStack, Cube, Gem, L0, L1, L2, Pill, Rays, Sparkle, Star5, Streaks, type SceneProps } from "./prims";

/* ─────────────── EduPoly: a board in 3D with houses, dice and coins ─────────────── */
export function Monopoly({ u }: SceneProps) {
  const S = 150, ring = 44, SC = 1.16;
  const P = (x: number, y: number): [number, number] => [320 + (x - y) * 0.78 * SC, 196 + (x + y) * 0.39 * SC];
  const poly = (pts: [number, number][]) => pts.map((p) => p.join(",")).join(" ");
  const COLORS = ["#8a4b2a", "#58c7f0", "#ec5fa6", "#ff9a2e", "#ef4444", "#ffd23f", "#3dbf6a", "#2f5be0"];
  const tiles: ReactNode[] = [];
  const centers: [number, number][] = [];
  const edge = (side: number) => {
    const n = 5, len = (2 * S - 2 * ring) / n;
    for (let i = 0; i < n; i++) {
      const a = -S + ring + i * len, b = a + len;
      let q: [number, number][], cap: [number, number][];
      const o1 = S - ring, o2 = S; // inner and outer coordinate of the ring
      if (side === 0) { q = [[a, -o2], [b, -o2], [b, -o1], [a, -o1]]; cap = [[a, -o1 - 13], [b, -o1 - 13], [b, -o1], [a, -o1]]; }
      else if (side === 1) { q = [[o1, a], [o1, b], [o2, b], [o2, a]]; cap = [[o1, a], [o1, b], [o1 + 13, b], [o1 + 13, a]]; }
      else if (side === 2) { q = [[a, o1], [b, o1], [b, o2], [a, o2]]; cap = [[a, o1], [b, o1], [b, o1 + 13], [a, o1 + 13]]; }
      else { q = [[-o2, a], [-o2, b], [-o1, b], [-o1, a]]; cap = [[-o1 - 13, a], [-o1 - 13, b], [-o1, b], [-o1, a]]; }
      const col = COLORS[(side * 5 + i) % COLORS.length]!;
      tiles.push(
        <g key={`${side}-${i}`}>
          <polygon points={poly(q.map(([x, y]) => P(x, y)))} fill="#fff7e0" stroke="#d9c9a0" strokeWidth={1.2} />
          <polygon points={poly(cap.map(([x, y]) => P(x, y)))} fill={col} stroke="#fff7e0" strokeWidth={1} />
        </g>,
      );
      const cx = q.reduce((s, p) => s + p[0], 0) / 4, cy = q.reduce((s, p) => s + p[1], 0) / 4;
      centers.push(P(cx, cy));
    }
  };
  [0, 1, 2, 3].forEach(edge);
  const corner = (x: number, y: number, k: number) => (
    <g key={`c${k}`}>
      <polygon points={poly([[x, y], [x + (x < 0 ? ring : -ring), y], [x + (x < 0 ? ring : -ring), y + (y < 0 ? ring : -ring)], [x, y + (y < 0 ? ring : -ring)]].map(([a, b]) => P(a!, b!)))} fill="#ffe27a" stroke="#d9a400" strokeWidth={1.4} />
    </g>
  );
  const house = (c: [number, number], k: number, hotel = false) => {
    const [x, y] = c, w = hotel ? 15 : 10, h = hotel ? 24 : 13;
    return (
      <g key={k} transform={`translate(${x} ${y - 2})`}>
        <ellipse cx={2} cy={4} rx={w + 4} ry={5} fill="#000" opacity={0.18} />
        <polygon points={`${-w},${-h + 3} 0,${-h + 3 + w * 0.5} 0,${w * 0.5} ${-w},0`} fill="#fff4e0" />
        <polygon points={`${w},${-h + 3} 0,${-h + 3 + w * 0.5} 0,${w * 0.5} ${w},0`} fill="#e9cfa6" />
        <polygon points={`${-w - 2},${-h + 4} 0,${-h - w * 0.55} ${w + 2},${-h + 4} 0,${-h + 4 + w * 0.55}`} fill={hotel ? "#ff7a1a" : "#ef3b3b"} />
        <polygon points={`${-w - 2},${-h + 4} 0,${-h + 4 + w * 0.55} 0,${-h + 8 + w * 0.55} ${-w - 2},${-h + 8}`} fill={hotel ? "#c94f00" : "#b92424"} />
        {hotel && <><rect x={-w + 3} y={-h + 8} width={4} height={5} fill="#7ee8ff" /><rect x={w - 7} y={-h + 8} width={4} height={5} fill="#7ee8ff" /></>}
      </g>
    );
  };
  const pips = (face: "t" | "l" | "r", n: number): ReactNode => {
    // pips of a die as small dark circles on the right projection of the face
    const spots: Record<number, [number, number][]> = { 1: [[0, 0]], 2: [[-0.45, -0.45], [0.45, 0.45]], 3: [[-0.5, -0.5], [0, 0], [0.5, 0.5]], 5: [[-0.5, -0.5], [0.5, -0.5], [0, 0], [-0.5, 0.5], [0.5, 0.5]], 4: [[-0.5, -0.5], [0.5, -0.5], [-0.5, 0.5], [0.5, 0.5]], 6: [[-0.5, -0.55], [0.5, -0.55], [-0.5, 0], [0.5, 0], [-0.5, 0.55], [0.5, 0.55]] };
    const s = 34, w = s * 0.87, h = s * 0.5;
    const m = face === "t" ? `matrix(${w / 2} ${h / 2} ${-w / 2} ${h / 2} 0 ${-s + h})` : face === "l" ? `matrix(${w / 2} ${h / 2} 0 ${s / 2} ${-w / 2} ${h / 2})` : `matrix(${w / 2} ${-h / 2} 0 ${s / 2} ${w / 2} ${h / 2})`;
    return <g transform={m} fill="#1b2a6b">{(spots[n] ?? spots[1]!).map(([a, b], i) => <circle key={i} cx={a} cy={b} r={0.2} />)}</g>;
  };
  const houses: [number, boolean][] = [[1, false], [3, false], [7, true], [12, false], [14, false], [17, true]];
  return (
    <>
      <Bg u={u} a="#9bf0b2" b="#1f9d62" glow={0.7} cy={38} />
      <L0>
        <Rays cx={320} cy={150} n={20} len={560} opacity={0.14} />
        <Cloud x={92} y={62} s={0.8} o={0.85} />
        <Cloud x={552} y={44} s={0.6} o={0.8} />
        <Sparkle x={150} y={150} s={10} /><Sparkle x={560} y={250} s={8} />
      </L0>
      <L1>
        {/* the board: slab, ring of tiles and the green middle */}
        <polygon points={poly([P(-S, -S), P(S, -S), P(S, S), P(-S, S)].map(([x, y]) => [x, y + 16] as [number, number]))} fill="#0f6b3e" />
        <polygon points={poly([P(-S, S), P(S, S)].concat([[P(S, S)[0], P(S, S)[1] + 16], [P(-S, S)[0], P(-S, S)[1] + 16]]))} fill="#14804a" />
        <polygon points={poly([P(-S, -S), P(S, -S), P(S, S), P(-S, S)])} fill="#fff7e0" stroke="#fff" strokeWidth={3} strokeLinejoin="round" />
        <polygon points={poly([P(-S + ring, -S + ring), P(S - ring, -S + ring), P(S - ring, S - ring), P(-S + ring, S - ring)])} fill="#58d68d" stroke="#2fa866" strokeWidth={1.5} />
        <polygon points={poly([P(-S + ring + 14, -S + ring + 14), P(S - ring - 14, -S + ring + 14), P(S - ring - 14, S - ring - 14), P(-S + ring + 14, S - ring - 14)])} fill="#7ee8a6" opacity={0.6} />
        {tiles}
        {corner(-S, -S, 0)}{corner(S, -S, 1)}{corner(S, S, 2)}{corner(-S, S, 3)}
        {houses.map(([i, hotel], k) => house(centers[i]!, k, hotel))}
        {/* two pawns */}
        {[[centers[9]!, "#2f7bff"], [centers[4]!, "#ff5c8a"]].map(([c, col], k) => {
          const [x, y] = c as [number, number];
          return (
            <g key={k} transform={`translate(${x + 2} ${y})`}>
              <ellipse cx={0} cy={3} rx={9} ry={4} fill="#000" opacity={0.2} />
              <path d="M-8 2 Q-8 -8 -3 -12 L3 -12 Q8 -8 8 2 Z" fill={col as string} />
              <circle cx={0} cy={-18} r={7} fill={col as string} />
              <circle cx={-2.4} cy={-20} r={2.2} fill="#fff" opacity={0.7} />
            </g>
          );
        })}
      </L1>
      <L2>
        {/* the golden question coin above the board – the questions that open the game */}
        <ellipse cx={320} cy={206} rx={40} ry={11} fill="#000" opacity={0.16} />
        <g transform="translate(320 148)">
          <circle r={50} cy={6} fill="#b86500" />
          <circle r={50} fill="#ffc61a" stroke="#d98200" strokeWidth={5} />
          <circle r={38} fill="#ffe27a" stroke="#e29a00" strokeWidth={3} />
          <text y={22} textAnchor="middle" fontSize={62} fill="#e07b00" style={{ fontFamily: "var(--display)" }}>?</text>
          <path d="M-36 -22 A40 40 0 0 1 -6 -42" fill="none" stroke="#fff" strokeOpacity={0.85} strokeWidth={6} strokeLinecap="round" />
        </g>
        <Cube x={528} y={92} s={30} rot={12} mark={(f) => pips(f, f === "t" ? 5 : f === "l" ? 3 : 2)} />
        <Cube x={578} y={142} s={26} rot={-14} mark={(f) => pips(f, f === "t" ? 6 : f === "l" ? 4 : 1)} />
        <CoinStack x={78} y={318} n={5} r={24} />
        <CoinStack x={124} y={332} n={3} r={22} />
        <Coin x={168} y={300} r={20} rot={-18} />
        <Gem x={566} y={312} s={18} a="#ffb3d1" b="#e0407f" />
        <Sparkle x={236} y={60} s={13} /><Sparkle x={420} y={40} s={9} fill="#ffe27a" />
      </L2>
    </>
  );
}

/* ─────────────── Kortkamp: a fan of cards, a "+2" with a question and the EDU! shout ─────────────── */
function PlayCard({ x, y, rot, color, dark, label, corner, big = 54, mark }: { x: number; y: number; rot: number; color: string; dark: string; label: string; corner: string; big?: number; mark?: ReactNode }) {
  return (
    <g transform={`translate(${x} ${y}) rotate(${rot})`}>
      <rect x={-60} y={-86} width={120} height={172} rx={16} fill="#000" opacity={0.16} transform="translate(4 7)" />
      <rect x={-60} y={-86} width={120} height={172} rx={16} fill="#fff" />
      <rect x={-52} y={-78} width={104} height={156} rx={11} fill={color} />
      <ellipse cx={0} cy={0} rx={40} ry={52} fill="#fff" opacity={0.95} transform="rotate(24)" />
      <text y={big * 0.36} textAnchor="middle" fontSize={big} fill={dark} style={{ fontFamily: "var(--display)" }}>{label}</text>
      <text x={-44} y={-56} fontSize={22} fill="#fff" style={{ fontFamily: "var(--display)" }}>{corner}</text>
      <text x={44} y={70} textAnchor="end" fontSize={22} fill="#fff" style={{ fontFamily: "var(--display)" }} transform="rotate(180 0 0) translate(0 0)" opacity={0} />
      <path d="M-42 -66 q0 -8 8 -8 h22" fill="none" stroke="#fff" strokeOpacity={0.5} strokeWidth={4} strokeLinecap="round" />
      {mark}
    </g>
  );
}
export function Kortkamp({ u }: SceneProps) {
  return (
    <>
      <Bg u={u} a="#ff8a6b" b="#ffbf47" glow={0.65} cy={55} />
      <L0>
        <Rays cx={320} cy={250} n={22} len={600} opacity={0.17} rot={0.1} />
        {[[70, 70], [560, 80], [90, 250], [590, 270]].map(([x, y], i) => <Star5 key={i} x={x!} y={y!} r={i % 2 ? 12 : 16} fill="#fff3a8" stroke="#ffd23f" />)}
        <Sparkle x={230} y={40} s={12} /><Sparkle x={420} y={30} s={9} />
      </L0>
      <L1>
        <PlayCard x={168} y={246} rot={-34} color="#3b82f6" dark="#1e50c8" label="5" corner="5" />
        <PlayCard x={232} y={222} rot={-17} color="#22c55e" dark="#15803d" label="9" corner="9" />
        <PlayCard x={408} y={222} rot={17} color="#a855f7" dark="#7a2fd0" label="3" corner="3" />
        <PlayCard x={472} y={246} rot={34} color="#ef4444" dark="#c42020" label="7" corner="7" />
        <PlayCard x={320} y={208} rot={0} color="#ffd23f" dark="#e07b00" label="+2" corner="+2" big={52} mark={
          <g transform="translate(36 -64)"><circle r={19} fill="#ff4d6d" stroke="#fff" strokeWidth={4} /><text y={9} textAnchor="middle" fontSize={26} fill="#fff" style={{ fontFamily: "var(--display)" }}>?</text></g>
        } />
      </L1>
      <L2>
        {/* the card that has just been played, in the air */}
        <g transform="translate(548 94) rotate(18) scale(0.62)">
          <PlayCard x={0} y={0} rot={0} color="#1f2a44" dark="#1f2a44" label="?" corner="★" big={70} />
        </g>
        {/* EDU! */}
        <g transform="translate(104 86) rotate(-8)">
          <path d="M-72 -34 Q-72 -50 -56 -50 H56 Q72 -50 72 -34 V16 Q72 32 56 32 H20 L34 58 L-14 32 H-56 Q-72 32 -72 16Z" fill="#fff" stroke="#14246a" strokeWidth={5} strokeLinejoin="round" />
          <text y={14} textAnchor="middle" fontSize={44} fill="#ef2d56" style={{ fontFamily: "var(--display)" }}>EDU!</text>
        </g>
        <Sparkle x={560} y={200} s={11} fill="#fff" /><Sparkle x={70} y={300} s={10} />
      </L2>
    </>
  );
}

/* ─────────────── Sænke slagskibe: a battleship being hit, a splash and a target ─────────────── */
export function Slagskibe({ u }: SceneProps) {
  const grid: ReactNode[] = [];
  for (let i = -8; i <= 8; i++) grid.push(<line key={`v${i}`} x1={320 + i * 18} y1={168} x2={320 + i * 96} y2={362} />);
  [176, 190, 208, 232, 266, 312].forEach((y) => grid.push(<line key={`h${y}`} x1={0} y1={y} x2={640} y2={y} />));
  return (
    <>
      <defs>
        <linearGradient id={`${u}sea`} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#52d6ff" /><stop offset="1" stopColor="#1546c4" /></linearGradient>
        <linearGradient id={`${u}sky`} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#bfeeff" /><stop offset="1" stopColor="#6dc8ff" /></linearGradient>
        <radialGradient id={`${u}boom`} cx="50%" cy="50%" r="50%"><stop offset="0" stopColor="#fff" /><stop offset="0.35" stopColor="#ffe45c" /><stop offset="0.7" stopColor="#ff8a1f" /><stop offset="1" stopColor="#ef3b2d" /></radialGradient>
      </defs>
      <L0>
        <rect width="640" height="170" fill={`url(#${u}sky)`} />
        <rect y="166" width="640" height="200" fill={`url(#${u}sea)`} />
        <Cloud x={120} y={56} s={0.9} /><Cloud x={470} y={40} s={0.7} o={0.8} /><Cloud x={590} y={96} s={0.5} o={0.7} />
        <g stroke="#fff" strokeOpacity={0.3} strokeWidth={1.6}>{grid}</g>
        <path d="M0 166 H640" stroke="#fff" strokeOpacity={0.6} strokeWidth={3} />
        {/* the far ship with the target on it */}
        <g transform="translate(500 166)">
          <path d="M-46 0 L46 0 L36 14 L-38 14Z" fill="#6c7a96" />
          <rect x={-18} y={-12} width={30} height={12} fill="#a9b6d1" /><rect x={-6} y={-22} width={12} height={10} fill="#c8d2e6" /><rect x={-26} y={-6} width={10} height={6} fill="#8a98b4" />
          <path d="M-46 14 h82 l-4 6 h-74Z" fill="#c0392b" opacity={0.85} />
        </g>
      </L0>
      <L1>
        {/* the near battleship, side view, bow to the right */}
        <ellipse cx={246} cy={286} rx={206} ry={16} fill="#0c2f8f" opacity={0.35} />
        <path d="M60 220 H430 Q462 220 482 196 L470 262 Q466 280 444 282 H96 Q72 282 66 262Z" fill="#7b88a6" />
        <path d="M66 262 Q72 282 96 282 H444 Q466 280 470 262 L472 250 H64Z" fill="#c9372c" />
        <path d="M64 250 H472 V256 H64Z" fill="#fff" opacity={0.5} />
        <path d="M60 220 H430 Q462 220 482 196 L486 206 Q462 230 430 232 H60Z" fill="#a9b6d1" />
        {/* superstructure */}
        <rect x={190} y={176} width={116} height={46} rx={4} fill="#c3cde2" />
        <rect x={214} y={146} width={66} height={32} rx={4} fill="#dfe6f4" />
        <rect x={232} y={122} width={30} height={26} rx={3} fill="#9fb0cf" />
        <line x1={247} y1={122} x2={247} y2={86} stroke="#59657f" strokeWidth={4} /><circle cx={247} cy={84} r={5} fill="#ef4444" />
        {[200, 224, 248, 272, 288].map((x) => <rect key={x} x={x} y={186} width={9} height={9} rx={2} fill="#7ee8ff" />)}
        <rect x={150} y={150} width={22} height={70} fill="#ffcf3f" /><rect x={150} y={150} width={22} height={14} fill="#3a4666" /><rect x={150} y={176} width={22} height={8} fill="#ef4444" opacity={0.0} />
        {/* turrets */}
        <g><rect x={318} y={196} width={46} height={26} rx={8} fill="#8996b3" /><rect x={350} y={200} width={74} height={7} rx={3} fill="#4b5873" transform="rotate(-18 350 203)" /><rect x={350} y={210} width={74} height={7} rx={3} fill="#4b5873" transform="rotate(-18 350 213)" /></g>
        <g><rect x={96} y={198} width={42} height={24} rx={8} fill="#8996b3" /><rect x={128} y={202} width={60} height={7} rx={3} fill="#4b5873" transform="rotate(-14 128 205)" /></g>
        <path d="M0 292 q40 -12 80 0 t80 0 t80 0 t80 0 t80 0 t80 0 t80 0 t80 0 V360 H0Z" fill="#2a7de8" opacity={0.85} />
        <path d="M0 316 q40 -10 80 0 t80 0 t80 0 t80 0 t80 0 t80 0 t80 0 t80 0 V360 H0Z" fill="#1d5fd0" opacity={0.9} />
      </L1>
      <L2>
        {/* hit: fire and smoke */}
        <circle cx={420} cy={150} r={28} fill="#5a6580" opacity={0.7} /><circle cx={448} cy={112} r={22} fill="#6b7692" opacity={0.6} /><circle cx={470} cy={78} r={17} fill="#7d88a4" opacity={0.5} />
        <g transform="translate(404 206)">
          <polygon points="0,-62 14,-30 46,-48 34,-14 70,-6 38,14 56,46 22,34 8,66 -8,36 -40,56 -30,22 -68,10 -34,-8 -48,-44 -14,-30" fill={`url(#${u}boom)`} stroke="#fff" strokeWidth={3} strokeLinejoin="round" />
          <circle r={14} fill="#fff" />
        </g>
        {/* miss: a column of water */}
        <g transform="translate(558 300)">
          <path d="M-22 0 Q-16 -52 0 -78 Q16 -52 22 0Z" fill="#fff" /><path d="M-12 0 Q-8 -36 0 -56 Q8 -36 12 0Z" fill="#bfeeff" />
          {[[-34, -40], [32, -50], [-48, -14], [46, -20], [-10, -96], [18, -90]].map(([x, y], i) => <circle key={i} cx={x} cy={y} r={5 - (i % 3)} fill="#fff" />)}
          <ellipse cy={4} rx={44} ry={9} fill="#fff" opacity={0.75} />
        </g>
        {/* target */}
        <g transform="translate(500 168)" stroke="#ff2d55" strokeWidth={4.5} fill="none" strokeLinecap="round">
          <circle r={34} /><circle r={16} strokeWidth={3} />
          <path d="M0 -46 V-24 M0 24 V46 M-46 0 H-24 M24 0 H46" />
          <circle r={3.5} fill="#ff2d55" stroke="none" />
        </g>
        <Sparkle x={84} y={100} s={10} /><Sparkle x={604} y={30} s={9} />
      </L2>
    </>
  );
}

/* ─────────────── Speedrun: a bolt, a stopwatch and answers flying ─────────────── */
export function Speedrun({ u }: SceneProps) {
  return (
    <>
      <Bg u={u} a="#ffe866" b="#ff8a1f" glow={0.75} />
      <L0>
        <Rays cx={320} cy={180} n={32} len={620} opacity={0.22} spread={0.03} />
        <Streaks items={[[20, 70, 130], [60, 108, 90], [8, 250, 150], [90, 290, 100], [500, 60, 110], [540, 300, 80], [470, 330, 130]]} color="#fff" o={0.55} />
      </L0>
      <L1>
        {/* the bolt */}
        <polygon points="352,24 214,198 298,198 262,336 428,142 338,142" fill="#b84a00" transform="translate(8 10)" opacity={0.55} />
        <polygon points="352,24 214,198 298,198 262,336 428,142 338,142" fill="#fff3a0" stroke="#fff" strokeWidth={14} strokeLinejoin="round" />
        <polygon points="352,24 214,198 298,198 262,336 428,142 338,142" fill="#ffd62e" stroke="#e08e00" strokeWidth={5} strokeLinejoin="round" />
        <polygon points="344,52 252,178 316,178" fill="#fff" opacity={0.55} />
      </L1>
      <L2>
        {/* stopwatch */}
        <g transform="translate(130 232)">
          <rect x={-10} y={-92} width={20} height={16} rx={4} fill="#3a4a8a" />
          <rect x={-18} y={-98} width={36} height={9} rx={4} fill="#5a6ab0" />
          <circle r={70} cy={5} fill="#0c1850" opacity={0.25} />
          <circle r={68} fill="#14246a" />
          <circle r={58} fill="#fff" />
          {Array.from({ length: 12 }, (_, i) => { const a = (i / 12) * Math.PI * 2; return <line key={i} x1={Math.sin(a) * 47} y1={-Math.cos(a) * 47} x2={Math.sin(a) * 54} y2={-Math.cos(a) * 54} stroke="#14246a" strokeWidth={i % 3 ? 3 : 5} strokeLinecap="round" />; })}
          <path d="M0 0 L0 -50 A50 50 0 0 1 43 25Z" fill="#ff5c3a" opacity={0.9} />
          <line x1={0} y1={0} x2={34} y2={-34} stroke="#14246a" strokeWidth={6} strokeLinecap="round" /><circle r={7} fill="#14246a" />
        </g>
        {/* right answers on their way */}
        {[[520, 130, 40], [566, 214, 30], [500, 262, 24]].map(([x, y, r], i) => (
          <g key={i}>
            <Streaks items={[[x! - r! * 2.6, y! - 6, r! * 1.6], [x! - r! * 2.2, y! + 8, r! * 1.3]]} color="#fff" o={0.9} />
            <circle cx={x} cy={y! + 4} r={r} fill="#0b6b2e" opacity={0.35} />
            <circle cx={x} cy={y} r={r} fill="#2ed36a" stroke="#fff" strokeWidth={r! * 0.16} />
            <path d={`M${x! - r! * 0.45} ${y! + r! * 0.02} l${r! * 0.3} ${r! * 0.34} l${r! * 0.62} ${-r! * 0.68}`} fill="none" stroke="#fff" strokeWidth={r! * 0.22} strokeLinecap="round" strokeLinejoin="round" />
          </g>
        ))}
        <Star5 x={76} y={90} r={16} fill="#fff6bd" stroke="#ffb000" /><Star5 x={590} y={60} r={13} fill="#fff6bd" stroke="#ffb000" />
        <Sparkle x={212} y={60} s={11} /><Sparkle x={440} y={320} s={12} />
      </L2>
    </>
  );
}

/* ─────────────── Fire på stribe: the board with a winning diagonal and a falling disc ─────────────── */
export function Fire({ u }: SceneProps) {
  const R = "#ff4d6d", Y = "#ffd23f";
  // columns 0..6, rows 0..5 (top → bottom); null = empty
  const grid: (string | null)[][] = [
    [null, null, null, null, null, null, null],
    [null, null, null, null, null, null, null],
    [null, null, null, null, null, null, Y],
    [null, null, null, null, null, Y, R],
    [null, R, Y, null, Y, R, R],
    [R, Y, R, Y, R, R, Y],
  ];
  // the yellow diagonal (col 3,row 5) (4,4) (5,3) (6,2)
  const win = [[3, 5], [4, 4], [5, 3], [6, 2]].map(([c, r]) => [190 + c! * 40, 100 + r! * 40] as [number, number]);
  const holes: ReactNode[] = [];
  grid.forEach((row, r) => row.forEach((c, k) => {
    const x = 190 + k * 40, y = 100 + r * 40;
    holes.push(<circle key={`h${r}${k}`} cx={x} cy={y} r={17} fill="#16288f" />);
    if (c) {
      holes.push(
        <g key={`d${r}${k}`}>
          <circle cx={x} cy={y} r={17} fill={c} stroke={c === R ? "#c42040" : "#e0a400"} strokeWidth={2.5} />
          <circle cx={x} cy={y} r={10.5} fill="none" stroke={c === R ? "#ff8aa0" : "#fff0a0"} strokeWidth={2.4} />
          <path d={`M${x - 11} ${y - 4} A12 12 0 0 1 ${x - 2} ${y - 12}`} fill="none" stroke="#fff" strokeOpacity={0.8} strokeWidth={2.6} strokeLinecap="round" />
        </g>,
      );
    }
  }));
  return (
    <>
      <Bg u={u} a="#9b7bff" b="#3fc8ee" glow={0.55} />
      <L0>
        <Rays cx={320} cy={180} n={16} len={560} opacity={0.12} />
        {[[70, 80, 22], [572, 70, 18], [86, 250, 16], [566, 262, 24]].map(([x, y, r], i) => (
          <g key={i}><circle cx={x} cy={y} r={r} fill="#fff" opacity={0.28} /><text x={x} y={y! + r! * 0.4} textAnchor="middle" fontSize={r! * 1.2} fill="#fff" style={{ fontFamily: "var(--display)" }}>?</text></g>
        ))}
      </L0>
      <L1>
        <rect x={188} y={318} width={22} height={34} rx={5} fill="#0f1f78" /><rect x={430} y={318} width={22} height={34} rx={5} fill="#0f1f78" />
        <rect x={168} y={70} width={304} height={262} rx={24} fill="#0a1766" transform="translate(0 7)" />
        <rect x={168} y={70} width={304} height={262} rx={24} fill="#2a4be0" stroke="#7fa2ff" strokeWidth={4} />
        <rect x={178} y={80} width={284} height={242} rx={16} fill="none" stroke="#fff" strokeOpacity={0.22} strokeWidth={3} />
        {holes}
        <polyline points={win.map((p) => p.join(",")).join(" ")} fill="none" stroke="#fff" strokeWidth={13} strokeLinecap="round" strokeLinejoin="round" opacity={0.95} />
        <polyline points={win.map((p) => p.join(",")).join(" ")} fill="none" stroke="#ff8a00" strokeWidth={6} strokeLinecap="round" strokeLinejoin="round" />
        {win.map(([x, y], i) => <circle key={i} cx={x} cy={y} r={22} fill="none" stroke="#fff" strokeWidth={3.5} strokeDasharray="5 4" />)}
      </L1>
      <L2>
        {/* the disc that is dropping */}
        <g transform="translate(270 34)">
          <path d="M-11 -24 v-22 M0 -30 v-30 M11 -24 v-22" stroke="#fff" strokeOpacity={0.8} strokeWidth={5} strokeLinecap="round" />
          <circle r={19} cy={4} fill="#000" opacity={0.15} />
          <circle r={19} fill={R} stroke="#c42040" strokeWidth={3} /><circle r={11.5} fill="none" stroke="#ff8aa0" strokeWidth={2.6} />
          <path d="M-12 -5 A13 13 0 0 1 -2 -13" fill="none" stroke="#fff" strokeOpacity={0.85} strokeWidth={3} strokeLinecap="round" />
        </g>
        <Star5 x={522} y={118} r={15} fill="#fff6bd" stroke="#ffb000" /><Star5 x={118} y={150} r={12} fill="#fff6bd" stroke="#ffb000" />
        <Sparkle x={500} y={300} s={12} /><Sparkle x={140} y={310} s={9} /><Sparkle x={556} y={190} s={8} />
      </L2>
    </>
  );
}
